import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Users, TrendingUp, DollarSign, Copy, ExternalLink, Loader2, AlertTriangle, Check, BadgeCheck } from "lucide-react";

export default function CreatorDashboard() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/creators/dashboard/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Couldn't load your dashboard"));
  }, [token]);

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }
    catch { toast.error("Couldn't copy"); }
  };

  if (err) return (
    <div className="min-h-screen grid place-items-center p-6">
      <div className="cp-card p-6 text-center max-w-sm" data-testid="creator-dashboard-error">
        <AlertTriangle className="w-10 h-10 text-[#C2185B] mx-auto mb-3" />
        <div className="font-semibold text-[#3B2C24]">{err}</div>
        <Link to="/" className="text-[#FF7E67] font-semibold hover:underline text-sm mt-4 inline-block">Back home</Link>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#857163]" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF0E0] via-white to-[#FFD9C4] py-8 sm:py-14 px-4" data-testid="creator-dashboard">
      <div className="max-w-2xl mx-auto">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F4A83B]/20 text-[#B26A00] text-xs uppercase tracking-widest font-bold mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Creator Dashboard
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#3B2C24]">Hey, {data.name}! 👋</h1>
          <p className="mt-2 text-sm text-[#857163]">Here's how your Cake Pop Talk sign-ups are performing.</p>
        </div>

        {/* Code + link */}
        <div className="cp-card p-5 sm:p-6 mb-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#857163] mb-1">Your promo code</div>
              <button onClick={() => copy(data.promo_code)} className="w-full text-left px-4 py-3 rounded-2xl bg-[#FFD07F]/40 border border-[#F4A83B]/40 hover:bg-[#FFD07F]/60 transition font-heading text-2xl font-bold text-[#3B2C24] flex items-center justify-between" data-testid="creator-copy-code">
                {data.promo_code} <Copy className="w-4 h-4 text-[#857163]" />
              </button>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[#857163] mb-1">Your referral link</div>
              <button onClick={() => copy(data.referral_url)} className="w-full text-left px-4 py-3 rounded-2xl bg-[#EAF3FE] border border-[#2F80ED]/30 hover:bg-[#EAF3FE]/80 transition text-sm text-[#1D5FBF] break-all flex items-center justify-between gap-2" data-testid="creator-copy-link">
                <span className="truncate">{data.referral_url}</span> <Copy className="w-4 h-4 shrink-0" />
              </button>
            </div>
          </div>
          <div className="text-xs text-[#857163] mt-3">
            Users who sign up with your code or link get a <b>21-day free trial</b> (instead of 14). You earn <b>${data.payout_per_conversion.toFixed(2)}</b> for each paying conversion.
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <Stat icon={<Users className="w-5 h-5 text-[#8EAC50]" />} label="Sign-ups" value={data.signups} />
          <Stat icon={<TrendingUp className="w-5 h-5 text-[#2F80ED]" />} label="Paying" value={data.conversions} />
          <Stat icon={<DollarSign className="w-5 h-5 text-[#F4A83B]" />} label="Total earned" value={`$${data.earned.toFixed(2)}`} />
          <Stat icon={<DollarSign className="w-5 h-5 text-[#C2185B]" />} label={data.owed < 0 ? "Credit" : "Owed"} value={`$${Math.abs(data.owed).toFixed(2)}`} highlight={data.owed > 0} />
        </div>

        {/* Sign-up list */}
        <div className="cp-card p-5 sm:p-6 mb-5" data-testid="creator-signup-list">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-widest text-[#857163]">Your sign-ups</div>
            <div className="text-xs text-[#857163]">
              <span className="inline-flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5 text-[#2F80ED]" /> = paying customer</span>
            </div>
          </div>
          {(!data.signup_list || data.signup_list.length === 0) ? (
            <div className="text-sm text-[#857163]">No sign-ups yet. Share your promo code or referral link to get started.</div>
          ) : (
            <ul className="divide-y divide-[#EBDCD1]/70">
              {data.signup_list.map((s) => (
                <li key={s.username + (s.joined_at || "")} className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0" data-testid={`signup-row-${s.username}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-full grid place-items-center text-white font-heading text-sm shrink-0" style={{ background: s.converted ? "#2F80ED" : "#B79885" }}>
                      {(s.username || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 text-sm font-semibold text-[#3B2C24] truncate">
                        @{s.username}
                        {s.converted && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#EAF3FE] text-[10px] font-bold uppercase tracking-wide text-[#1D5FBF]" data-testid={`converted-badge-${s.username}`} title="Upgraded to a paying customer">
                            <BadgeCheck className="w-3 h-3" /> Paying
                          </span>
                        )}
                      </div>
                      {s.joined_at && (
                        <div className="text-[11px] text-[#857163]">
                          Joined {new Date(s.joined_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Payout history */}
        <div className="cp-card p-5 sm:p-6">
          <div className="text-xs uppercase tracking-widest text-[#857163] mb-3">Payment history</div>
          {data.payouts.length === 0 ? (
            <div className="text-sm text-[#857163]">No payments yet. Once you've earned some, this section will show every payout with the date and note.</div>
          ) : (
            <ul className="space-y-2">
              {data.payouts.map((p) => (
                <li key={p.id} className="flex items-center justify-between px-3 py-2 rounded-2xl bg-[#F1F7E5] border border-[#8EAC50]/20 text-sm">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#5E7A2E]" />
                    <span className="font-bold text-[#3B2C24]">${p.amount.toFixed(2)}</span>
                    {p.note && <span className="text-xs text-[#857163]">· {p.note}</span>}
                  </div>
                  <div className="text-xs text-[#857163]">{(p.paid_at || "").slice(0, 10)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="text-center text-xs text-[#857163] mt-8">
          <Link to={`/join/${data.referral_slug}`} className="inline-flex items-center gap-1 hover:underline">
            Share your referral link <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, highlight }) {
  return (
    <div className={`cp-card p-3 sm:p-4 ${highlight ? "ring-2 ring-[#C2185B]/30" : ""}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#857163]">{icon}{label}</div>
      <div className="text-xl sm:text-2xl font-bold text-[#3B2C24] mt-1">{value}</div>
    </div>
  );
}
